import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertCircle, FileDown, Loader2 } from "lucide-react";

import { PageHeader } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ApiError } from "@/integrations/aws/http";
import { getInvoiceRunDownloadUrl } from "@/integrations/aws/api";

export const Route = createFileRoute("/_authenticated/invoice-runs/$runId")({
  head: () => ({ meta: [{ title: "Bulk invoices — Somasi Ops" }] }),
  component: InvoiceRunDownload,
});

/**
 * Where a bulk run's email lands.
 *
 * Behind sign-in like every other page, so a forwarded email is no use to
 * anybody without an account. The server checks the run's expiry and answers
 * with a link to the PDF in S3 that lasts minutes; the download starts on
 * arrival, and the button asks again for a fresh one.
 */
function InvoiceRunDownload() {
  const { runId } = Route.useParams();
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "ready" }
    | { kind: "expired" }
    | { kind: "error"; message: string }
  >({ kind: "loading" });

  const download = async () => {
    setState({ kind: "loading" });
    try {
      window.location.assign(await getInvoiceRunDownloadUrl(runId));
      setState({ kind: "ready" });
    } catch (error) {
      if (error instanceof ApiError && error.status === 410) setState({ kind: "expired" });
      else setState({ kind: "error", message: (error as Error).message });
    }
  };

  useEffect(() => {
    void download();
  }, [runId]);

  return (
    <div>
      <PageHeader title="Bulk invoices" description="The combined PDF from a bulk invoicing run." />
      <Card className="max-w-xl">
        <CardContent className="space-y-4 pt-6 text-sm">
          {state.kind === "loading" && (
            <p className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Preparing the download…
            </p>
          )}
          {state.kind === "ready" && (
            <>
              <p>The download should have started.</p>
              <Button variant="outline" onClick={() => void download()}>
                <FileDown className="mr-2 h-4 w-4" /> Download again
              </Button>
            </>
          )}
          {state.kind === "expired" && (
            <p className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 text-warning" />
              This link has expired. The combined PDF is still on the{" "}
              <Link to="/bulk-actions" className="underline">
                Bulk Actions
              </Link>{" "}
              page, and each invoice on the{" "}
              <Link to="/invoices" className="underline">
                Invoices
              </Link>{" "}
              page.
            </p>
          )}
          {state.kind === "error" && (
            <p className="flex items-start gap-2 text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4" /> {state.message}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
